import * as React from 'react';

import { PlayArrow } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import httpLaunchRscript from 'consumers/httpLaunchRscript';
import { useSelector } from 'react-redux';
import selectGgEnvironment from 'selectors/ggEnvironment';
import selectSessionToken from 'selectors/sessionToken';
import selectTokenRoles from 'selectors/sessionTokenRoles';
import { jobDefinition } from 'utils/jobDefinition';
import { hasSuperAdminRole } from 'utils/roleUtils';

import { isRenderAbandoned, isRenderRunning } from './renderState';

/**
 * Render this translation.
 *
 * The row stores the request — which products, seen by which audience — and
 * this is what runs it. Rendering is a Batch job rather than something that
 * happens on save: it is products × grid cells of brms work, and the size of
 * that is chosen by whoever built the translation.
 *
 * The render is against the parent norm set's model, not the deployed one, so
 * an old norm set keeps producing numbers comparable with the ones it already
 * produced.
 *
 * conchBatch only accepts a superadmin, so anyone else is given the command
 * rather than a button that would 403.
 *
 * This launches and nothing more. The render owns the whole status lifecycle —
 * it claims the row itself, so nothing here writes STARTED. That is what lets
 * a job dying before its container starts leave the row exactly as it was,
 * still launchable, instead of stranded reading as running.
 */

const SCRIPT_PATH = 'batchjobs/trigger_3_render_translation.R';

type Props = {
  translationId: number;
  /** how many renders this will ask for, so the cost is stated up front */
  products: number;
  cells: number;
  /** REQUESTED is eligible; STARTED is already running */
  status: string;
  /** when the current run was accepted, so an abandoned one can be released */
  startedAt?: string | null;
  /** unsaved product edits would not be in the render */
  dirty?: boolean;
};

/**
 * The four states, and what the button does in each:
 *
 *   REQUESTED  the row exists and nothing has been asked of it — push it
 *   STARTED    the button has been pushed and a Batch job is running — wait
 *   SUCCESS    it rendered — push again to re-run
 *   FAILED     it did not — push again to retry, status_reason says why
 *
 * Only a run already under way blocks another. Re-running a finished report is
 * a real thing to want, since the products or the model behind it can move.
 *
 * "Under way" is age-sensitive: see renderState.ts for why a STARTED row past
 * the stale window has to stop counting as running.
 */

const TriggerReportButton: React.FC<Props> = ({
  translationId,
  products,
  cells,
  status,
  startedAt = null,
  dirty = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [launched, setLaunched] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const token = useSelector((state) => selectSessionToken(state));
  const roles = useSelector((state) => selectTokenRoles(state));
  const allowed = hasSuperAdminRole(roles);
  const environment = useSelector((state) => selectGgEnvironment(state));

  // A STARTED row past the stale window is treated as abandoned rather than
  // running, which is what makes R's stale-claim branch reachable.
  const abandoned = isRenderAbandoned(status, startedAt);
  // `launched` covers the gap between Batch accepting the submission and the
  // container claiming the row. Nothing writes STARTED in that window any more,
  // so without it the button would stay live and a second launch would cost a
  // Batch job to have claim_translation refuse it.
  const running = isRenderRunning(status, startedAt) || launched;

  const args = [`--translation_id=${translationId}`];
  const command = `Rscript ${SCRIPT_PATH} --translation_id=${translationId}`;

  const onSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await httpLaunchRscript(SCRIPT_PATH, args, token, {
        jobDefinition: environment ? jobDefinition(environment) : undefined,
      });
      setLaunched(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        startIcon={<PlayArrow />}
        disabled={running || !products}
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
      >
        {running
          ? 'Report running…'
          : abandoned
            ? 'Force re-run'
            : status === 'SUCCESS' || status === 'FAILED'
              ? 'Run report again'
              : 'Trigger report'}
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Render this translation</DialogTitle>
        <DialogContent>
          {dirty && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              There are unsaved product changes. The render reads the saved row,
              so it would cover the products as last saved — save first if the
              change belongs in this report.
            </Alert>
          )}

          <DialogContentText gutterBottom>
            {products} {products === 1 ? 'product' : 'products'} × {cells}{' '}
            {cells === 1 ? 'cell' : 'cells'} = {products * cells} renders,
            against the model this norm set was built on rather than whatever is
            deployed now.
          </DialogContentText>

          {allowed ? (
            <DialogContentText variant="body2">
              This runs on AWS Batch and takes a while. It refuses a demographic
              level the model never trained on, and any product with no
              signature in this universe, rather than rendering them as
              something plausible and wrong.
            </DialogContentText>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 1 }}>
                Starting a render needs superadmin. Ask someone who has it, or
                run it directly:
              </Alert>
              <Box
                component="pre"
                sx={{
                  p: 1,
                  m: 0,
                  backgroundColor: 'grey.100',
                  borderRadius: 1,
                  fontSize: 12,
                  overflowX: 'auto',
                }}
              >
                {command}
              </Box>
            </>
          )}

          {launched && (
            <Alert severity="success" sx={{ mt: 1 }}>
              Submitted. The render picks the row up when its container starts,
              and moves it to STARTED then SUCCESS or FAILED.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>
            {launched ? 'Close' : 'Cancel'}
          </Button>
          {allowed && !launched && (
            <Button
              variant="contained"
              disabled={submitting}
              onClick={onSubmit}
            >
              {submitting ? 'Starting…' : 'Start render'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}
      >
        {running
          ? 'A render is already under way for this translation.'
          : abandoned
            ? 'A render started over 12 hours ago and never reported back. It is treated as abandoned — running again will take the row over.'
            : 'Renders this translation on AWS Batch.'}
      </Typography>
    </>
  );
};

export default TriggerReportButton;
